import { useCallback, useContext, useMemo, useRef } from 'react';

import { useConfluencePageAri } from '@atlassian/ai-summary';
import { useConvoAIStreaming, ConvoAIResponseState } from '@atlassian/ai-agent-streaming';
import type {
	UserConvoAIStreamingConfig,
	ConvoAIStreamingState,
} from '@atlassian/ai-agent-streaming';
import type {
	AiFeatureRequestEnvelopeEditorAiFeatureRequest,
	EditorAiAnswerPartResponseMessage,
	EditorAiErrorResponseMessage,
	EditorAiFinalResponseMessage,
} from '@atlassian/convo-ai-api';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { AI_TITLE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

interface UseTitleGenerationStreamingProps {
	contentId: string;
	cloudId: string;
	documentContext: string;
	locale: string;
	skip: boolean;
	onTitleChange?: (title: string) => void;
}

interface LockedRequestValues {
	documentContext: string;
	pageAri: string;
	skip: boolean;
}

const buildTitleGenerationRequest = (
	contentAri: string,
	documentContext: string,
	locale: string,
	skip: boolean,
): [AiFeatureRequestEnvelopeEditorAiFeatureRequest, UserConvoAIStreamingConfig] => [
	{
		ai_feature_input: {
			feature: 'suggest_title',
			feature_args: {
				user_input: '',
				document_context: documentContext,
				supportedMarkdownNodes: ['PARAGRAPH', 'HEADING', 'BULLET_LISTS'],
			},
			content_ari: contentAri || undefined,
			locale,
		},
	},
	{
		apiPath: 'v2/ai-feature/editor-ai/stream',
		delayChunkUpdate: { gap: 10, time: 20 },
		enableFollowUp: false,
		enablePartialJson: false,
		skip,
		headers: {
			'x-experience-id': 'editor',
			'x-product': 'confluence',
		},
	},
];

export const useTitleGenerationStreaming = ({
	contentId,
	cloudId,
	documentContext,
	locale,
	skip,
	onTitleChange,
}: UseTitleGenerationStreamingProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { trackAIResultError } = useAIEventsInstrumentation();
	const { pageAri } = useConfluencePageAri(cloudId, contentId, 'page');

	const requestOptionsRef = useRef<LockedRequestValues | null>(null);

	if (!requestOptionsRef.current && pageAri && documentContext) {
		requestOptionsRef.current = { documentContext, pageAri, skip };
	}

	// locking the request options to avoid rebuilding the request on every render
	// this is in the case the document context changes or the page ari loads from undefined to a value
	const requestOptions = requestOptionsRef.current;

	const [request, config] = useMemo(
		() =>
			buildTitleGenerationRequest(
				requestOptions?.pageAri ?? pageAri ?? '',
				requestOptions?.documentContext ?? documentContext,
				locale,
				requestOptions?.skip ?? skip,
			),
		[requestOptions, pageAri, documentContext, locale, skip],
	);

	const onStateChange = useCallback(
		(
			state: ConvoAIStreamingState<
				EditorAiFinalResponseMessage,
				EditorAiErrorResponseMessage,
				never,
				EditorAiAnswerPartResponseMessage
			>,
		) => {
			switch (state.responseState) {
				case ConvoAIResponseState.AnswerPart:
					break;
				case ConvoAIResponseState.FinalResponse:
					onTitleChange?.(state.content);
					experienceTracker?.succeed({ name: AI_TITLE_GENERATION });
					break;
				case ConvoAIResponseState.Error:
					const errorMessage = state.error?.message?.content || 'Title generation failed';
					if (!errorMessage.includes('missing valid content')) {
						trackAIResultError(
							{ aiErrorMessage: errorMessage },
							{ attributes: { aiFeatureName: 'aiTitleGenOnPaste' } },
						);
						experienceTracker?.fail({
							name: AI_TITLE_GENERATION,
							error: new Error(errorMessage),
						});
					}
					break;
			}
		},
		[onTitleChange, experienceTracker, trackAIResultError],
	);

	const [{ content, responseState, error }] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeEditorAiFeatureRequest,
		EditorAiFinalResponseMessage,
		EditorAiErrorResponseMessage,
		never,
		EditorAiAnswerPartResponseMessage
	>(request, config, onStateChange);

	return { content, responseState, error };
};
